/**
 * Viewer-local types + helpers for the annotated document viewer (source
 * provenance, spec #20423 Story 3).
 *
 * NOTE: These are owned by the document viewer. The Sources-panel rework owns
 * its own source types separately; do not import those here (and vice versa) to
 * avoid cross-team collisions on the same branch.
 */

/**
 * A single highlight rectangle on a page. Coordinate system assumption:
 * absolute pixels at the OCR/extraction resolution (the same space the backend
 * emits for `chunk_metadata.bounding_boxes`). The viewer scales these to the
 * rendered page width. If the backend later emits normalized 0–1 coordinates,
 * flip `BBOX_COORDS_ARE_NORMALIZED` below.
 */
export interface AnnotationBoundingBox {
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Coordinate space a single annotation's bounding boxes live in (#20423):
 * - "ocr_px": absolute pixels at the OCR/extraction resolution (default;
 *   scaled to the rendered page via intrinsic-vs-CSS measurement).
 * - "normalized": 0–1 fractions of the page (multiplied by the rendered size).
 *
 * Decided per chunk/annotation from `chunk_metadata.bbox_space`, since
 * historical/mixed ingestion paths may differ. When absent it defaults to
 * "ocr_px" (the long-standing behavior).
 */
export type BBoxSpace = "ocr_px" | "normalized";

/** Default coordinate space when an annotation carries no `bbox_space`. */
export const DEFAULT_BBOX_SPACE: BBoxSpace = "ocr_px";

/**
 * A cross-reference jump target attached to a cited annotation: a passage in
 * ANOTHER document cited for the same claim (chat-v2 mockup — the viewer
 * renders "↔ Cross-referenced with [N] …" under the highlight). Carries only
 * serializable display/jump data; the click handler resolves `chunkId` back to
 * its source through the provenance payload it already holds.
 */
export interface AnnotationCrossRef {
  chunkId: number;
  citationNumber: number;
  title: string;
  /** Sibling passage's section/section_title, when its chunk metadata has one. */
  section?: string;
}

/**
 * An annotation overlaid on the document. Produced by the caller from the
 * provenance/source payload (cited + read-but-not-cited chunks).
 */
export interface DocumentAnnotation {
  chunk_id: number | string;
  /** 1-indexed page the annotation lives on (matches page_number convention). */
  page: number;
  /** Exact regions on the page. When absent, a page-level banner is shown. */
  bounding_boxes?: AnnotationBoundingBox[];
  /**
   * Coordinate space for THIS annotation's `bounding_boxes`. From
   * `chunk_metadata.bbox_space`; defaults to {@link DEFAULT_BBOX_SPACE}
   * ("ocr_px") when absent. Lets mixed ingestion paths coexist in one document.
   */
  bbox_space?: BBoxSpace;
  /** true = cited in the answer (teal); false = read but not cited (amber). */
  cited: boolean;
  /** Citation number to show in the margin for cited passages. */
  citationNumber?: number;
  /**
   * Tabular/CSV row highlight (0-indexed into the CSV body, header excluded),
   * for the tabular viewer. Set only for tabular chunks that carry a resolvable
   * row key; PDF annotations leave it unset (#20423 Gap 11).
   */
  row?: number;
  /**
   * Cross-document siblings cited for the same claim, rendered as inline
   * "↔ Cross-referenced with [N]" jump links under the highlight (cited
   * annotations only; set by attachAnnotationCrossRefs in crossReferences.ts).
   */
  crossRefs?: AnnotationCrossRef[];
}

/**
 * Source classification (backend `classification` field). Drives the chip shown
 * in the citation hover tooltip (Story 2).
 */
export type SourceClassification =
  | "project_file"
  | "org_dataset"
  | "external_library"
  | "adhoc_upload";

export const SOURCE_CLASSIFICATION_LABELS: Record<
  SourceClassification,
  string
> = {
  project_file: "Project file",
  org_dataset: "Org dataset",
  external_library: "External library",
  adhoc_upload: "Ad hoc upload",
};

/**
 * Module-global coordinate-system fallback for bounding boxes, used only when a
 * given annotation carries no per-annotation {@link DocumentAnnotation.bbox_space}.
 * We assume absolute OCR-resolution pixels (see AnnotationBoundingBox); if a
 * deployment's boxes are uniformly normalized 0–1 and the backend isn't yet
 * tagging `bbox_space`, set true. Per-annotation `bbox_space` always wins.
 */
export const BBOX_COORDS_ARE_NORMALIZED = false;

/**
 * An author-made annotation overlaid on the document (Library Story 9), as the
 * overlay renders it. Distinct from {@link DocumentAnnotation} (agent
 * provenance): these come from `/record-annotations`, carry a note + author,
 * and are editable/deletable by their author. The host maps the wire shape
 * (`src/api/recordAnnotations`) into this before handing it to the viewer.
 */
export interface AuthorDocumentAnnotation {
  id: number;
  /** 1-indexed page of the first anchor box (jump/nominal page). */
  page: number;
  bounding_boxes: AnnotationBoundingBox[];
  bbox_space?: BBoxSpace;
  note: string | null;
  authorName: string | null;
  /** ISO create timestamp, shown alongside the author name. */
  createdAt: string | null;
  /** true when the viewing user authored it — gates edit/delete affordances. */
  isAuthor: boolean;
}

/** Visual treatments for the annotation kinds. Shared by overlays + legend. */
export const ANNOTATION_COLORS = {
  cited: {
    fill: "rgba(20, 184, 166, 0.22)", // teal
    border: "rgba(13, 148, 136, 0.9)",
    marker: "#0d9488",
    markerText: "#ffffff",
    label: "Cited",
  },
  read: {
    fill: "rgba(245, 158, 11, 0.18)", // amber
    border: "rgba(217, 119, 6, 0.8)",
    marker: "#d97706",
    markerText: "#ffffff",
    label: "Read (not cited)",
  },
  author: {
    fill: "rgba(139, 92, 246, 0.16)", // violet — third class: author notes
    border: "rgba(124, 58, 237, 0.85)",
    marker: "#7c3aed",
    markerText: "#ffffff",
    label: "Note",
  },
} as const;
